/**
 * 右 cockpit:写作规划 · 生成前最后确认(照 dept-reporter-create.html .cockpit)。
 * 目标 / 核心信息 / 依据地图(每条指回来源;无来源剔除项**必须可见**,F4 留痕不静默)/
 * 分段计划 + 积分预估 + 确认生成。
 */
import { Loader2, Sparkles } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  REPORTER_MAY_EXCEED_NOTE,
  reporterArticleCreditsApprox,
} from "@/entities/scene-card/model/creditRates";
import { EVIDENCE_ORIGIN_LABEL, evidenceLine, humanizeReporterText } from "../model/labels";
import type { WritingPlan } from "../model/parse";

interface Props {
  plan: WritingPlan | null;
  planning: boolean;
  generating: boolean;
  /** 素材整理还没跑完时规划/生成都不该可点(引导路径:整理→规划→生成)。 */
  materialReady: boolean;
  targetWords: number;
  onPlan: () => void;
  onGenerate: () => void;
}

export function PlanPanel({
  plan,
  planning,
  generating,
  materialReady,
  targetWords,
  onPlan,
  onGenerate,
}: Props) {
  return (
    <aside
      data-reporter-plan=""
      className="flex flex-col gap-3 rounded-[calc(var(--radius)+4px)] border bg-card p-4"
    >
      <div className="text-sm font-semibold">写作规划 · 生成前最后确认</div>

      {!plan && (
        <div className="rounded-lg border border-dashed border-border bg-background/50 px-3 py-4 text-center text-xs text-muted-foreground">
          {planning ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              正在规划(AI 按本篇素材动态定分段)…
            </span>
          ) : materialReady ? (
            "素材已整理,下一步生成写作规划。"
          ) : (
            "先整理素材,再生成写作规划。"
          )}
        </div>
      )}

      {plan && (
        <div className="flex flex-col gap-2.5 text-xs">
          <div>
            <b className="mb-0.5 block text-2xs font-semibold text-muted-foreground">目标</b>
            {plan.objective || "—"}
          </div>
          <div>
            <b className="mb-0.5 block text-2xs font-semibold text-muted-foreground">核心信息</b>
            {plan.coreMessage || "—"}
          </div>
          {plan.keywords.length > 0 && (
            <div>
              <b className="mb-0.5 block text-2xs font-semibold text-muted-foreground">
                覆盖关键词
              </b>
              {plan.keywords.join("、")}
            </div>
          )}
          {/* R1 明示:开了知识库但依据地图零命中 → 不装有 */}
          {plan.evidenceMap.length > 0 &&
            !plan.evidenceMap.some((e) => e.origin === "knowledge") && (
              <div className="text-2xs text-muted-foreground">
                本次未命中企业知识库,将仅基于素材与产品资料成稿。
              </div>
            )}
          <div>
            <b className="mb-1 block text-2xs font-semibold text-muted-foreground">
              依据地图(每条可指回来源)
            </b>
            <div className="flex flex-col gap-1.5">
              {plan.evidenceMap.map((e) => (
                <div
                  key={e.key}
                  title={e.source}
                  className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-2xs leading-relaxed"
                >
                  <span className="mr-1.5 rounded-full bg-primary/10 px-1.5 py-px text-2xs font-bold text-primary">
                    {EVIDENCE_ORIGIN_LABEL[e.origin] ?? e.origin}
                  </span>
                  {/* 1004 F23 #6:label / value 里的素材键与子类码换中文,不露 foundingBackground / BRAND_STORY */}
                  {evidenceLine(e)}
                </div>
              ))}
              {/* 无来源剔除项:留痕可见(F4)—— 用户得知道 AI 没用哪些、为什么 */}
              {plan.droppedEvidence.map((d) => (
                <div
                  key={d}
                  className="rounded-lg border border-destructive/50 bg-background px-2.5 py-1.5 text-2xs leading-relaxed"
                >
                  <span className="mr-1.5 rounded-full bg-destructive/10 px-1.5 py-px text-2xs font-bold text-destructive">
                    无来源
                  </span>
                  {humanizeReporterText(d)} → <b className="text-destructive">已从规划剔除</b>
                  ,不入正文
                </div>
              ))}
            </div>
          </div>
          {plan.sections.length > 0 && (
            <div>
              <b className="mb-0.5 block text-2xs font-semibold text-muted-foreground">分段计划</b>
              {plan.sections.length} 段 · {plan.sections.map((x) => x.heading).join(" → ")}
            </div>
          )}
        </div>
      )}

      <div className="mt-1 rounded-lg border border-border bg-background/60 px-3 py-2.5 text-center">
        <div className="text-sm">
          本篇预计 <b>{reporterArticleCreditsApprox(targetWords)} 积分</b>
        </div>
        <div className="text-2xs text-muted-foreground">生成 + 字数收敛 + 事实守卫 + 质量评分</div>
        <div className="text-2xs text-muted-foreground" data-testid="reporter-may-exceed">
          {REPORTER_MAY_EXCEED_NOTE}
        </div>
      </div>

      {plan ? (
        <>
          <Button
            className="w-full"
            disabled={generating || planning}
            onClick={onGenerate}
            data-reporter-generate=""
          >
            {generating ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            {generating ? "生成中…" : "确认规划,生成文章"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-xs"
            disabled={generating || planning}
            onClick={onPlan}
            data-reporter-make-plan=""
          >
            {planning && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            改过素材?重新规划
          </Button>
        </>
      ) : (
        /*
         * ★这颗是**当前这一步的主动作**,不是次级出口(0811 琳琳:「整理好的内容,生成写作规划的
         *   按钮不明显」)。原来给的是 `variant="secondary"` —— 灰底,和它上面那块积分预估框
         *   一个色阶,在「素材刚整理完、正等着下一步」的那一刻反而最不显眼。
         * ★下一步那颗「确认规划,生成文章」本来就是 default(实心主按钮);同一个位置、同样是
         *   「往前走」的动作,却一个实心一个灰底 —— 用户学到的规律在这儿断了。两颗对齐成主按钮。
         * ★不动 disabled 条件:素材没整理完就是不该点(引导路径 整理→规划→生成),
         *   把它做显眼不等于把闸拆了。
         */
        <Button
          className="w-full"
          disabled={!materialReady || planning || generating}
          onClick={onPlan}
          data-reporter-make-plan=""
        >
          {planning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          生成写作规划
        </Button>
      )}
      <p className="text-center text-2xs text-muted-foreground">
        {/* ★ 2026-08-26 批 L:原文写「规划约 1~2 积分/次」。那是 ×10 时代的实测值,
            新口径(×1.6)下大概率也偏高约 6 倍 —— 但我**没有编一个新数**:
            它是实测值不是可推导的常量,拍一个新数就是在造假单价(对客红线)。
            而且 1~2 这个"区间"宽度是 2 倍,已经不是区间是猜。
            token 计费本来就不该在按钮旁边报单价 —— 把数字让给确认卡,这里只说怎么计。
            同口径见 `creditRates.ts` 的 EXPLOSIVE_FULL_JOB_CREDITS_APPROX 注释:
            「token 计费一律报区间,不编单价(编一个数必然在某一端骗人)」。 */}
        规划不合适可改素材重新整理、重新规划(规划按实际用量计,以确认卡为准);确认后的分段计划就是生成的合同。
      </p>
    </aside>
  );
}
